import React, { useEffect, useRef, useState } from 'react';
import apiService from '../../services/apiService';
import QrStatsModal from '../../components/QrStatsModal';
import QrEditModal from '../../components/QrEditModal';

interface QrCode {
  id: number;
  slug: string;
  user_id: number;
  name: string;
  destination_url: string;
  is_dynamic: boolean;
  qr_dark: string;
  data_dark: string;
  output_size_px: number;
  logo_percent: number;
  quiet_zone: number;
  black_to_white: boolean;
  has_logo: boolean;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
  png_url: string;
}

interface LogoAnalysis {
  logo_s3_key: string;
  png_url: string | null;
  palette: string[];
}

const QrCodes: React.FC = () => {
  const [qrs, setQrs] = useState<QrCode[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<QrCode | null>(null);
  const [busy, setBusy] = useState(false);

  // New-QR form state
  const [logoSource, setLogoSource] = useState<'upload' | 'url' | 'none'>('none');
  const [logoUrl, setLogoUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState<LogoAnalysis | null>(null);
  const [qrDark, setQrDark] = useState('#000000');
  const [dataDark, setDataDark] = useState('#000000');
  const fileRef = useRef<HTMLInputElement | null>(null);

  const [statsForQr, setStatsForQr] = useState<QrCode | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  async function refresh(overrideShowArchived?: boolean) {
    const archived = overrideShowArchived ?? showArchived;
    try {
      const res = await apiService.get<QrCode[]>('/api/qrcodes', {
        params: { include: archived ? 'archived' : 'active' },
      });
      setQrs(res.data);
    } catch (e: any) {
      setError(e?.message || String(e));
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  function resetForm() {
    setLogoSource('none');
    setLogoUrl('');
    setAnalysis(null);
    setQrDark('#000000');
    setDataDark('#000000');
    if (fileRef.current) fileRef.current.value = '';
  }

  async function handleAnalyze() {
    setError(null);
    setAnalyzing(true);
    try {
      const form = new FormData();
      if (logoSource === 'url') {
        if (!logoUrl) {
          setError('Enter a logo URL first.');
          return;
        }
        form.append('logo_url', logoUrl);
      } else if (logoSource === 'upload') {
        const f = fileRef.current?.files?.[0];
        if (!f) {
          setError('Select a logo file first.');
          return;
        }
        form.append('logo', f);
      } else {
        return;
      }
      const res = await apiService.post<LogoAnalysis>('/api/qrcodes/analyze-logo', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAnalysis(res.data);
      if (res.data.palette.length >= 1) setQrDark(res.data.palette[0]);
      if (res.data.palette.length >= 2) setDataDark(res.data.palette[1]);
    } catch (e: any) {
      setError(e?.message || String(e));
    } finally {
      setAnalyzing(false);
    }
  }

  async function handleCreate(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const form = new FormData(e.currentTarget);
      // Replace checkboxes that the browser drops when unchecked
      if (!form.has('is_dynamic')) form.set('is_dynamic', 'false');
      if (!form.has('black_to_white')) form.set('black_to_white', 'false');
      // Force the controlled color values (so palette clicks stick)
      form.set('qr_dark', qrDark);
      form.set('data_dark', dataDark);
      // Logo source handling
      form.delete('logo');
      form.delete('logo_url');
      if (analysis) {
        form.set('logo_s3_key', analysis.logo_s3_key);
      } else if (logoSource === 'url' && logoUrl) {
        form.set('logo_url', logoUrl);
      } else if (logoSource === 'upload') {
        const f = fileRef.current?.files?.[0];
        if (f) form.set('logo', f);
      }
      await apiService.post<QrCode>('/api/qrcodes', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setShowNew(false);
      resetForm();
      await refresh();
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleArchive(id: number) {
    setBusy(true);
    try {
      await apiService.post(`/api/qrcodes/${id}/archive`, {});
      await refresh();
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleUnarchive(id: number) {
    setBusy(true);
    try {
      await apiService.post(`/api/qrcodes/${id}/unarchive`, {});
      await refresh();
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleDeletePermanent(id: number, name: string) {
    if (!confirm(`Permanently delete "${name}"?\n\nThe row is removed, the PNG is deleted from S3, and /r/{slug} will 404. Any printed QR pointing at it stops working. This can't be undone.`)) return;
    setBusy(true);
    try {
      await apiService.delete(`/api/qrcodes/${id}`);
      await refresh();
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={{ padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <h1 style={{ margin: 0, flex: 1 }}>QR Codes{showArchived ? ' — Archived' : ''}</h1>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#3a4252', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => { setShowArchived(e.target.checked); setShowNew(false); setEditing(null); }}
          /> Show archived
        </label>
        <button
          onClick={() => { setShowNew((v) => !v); if (showNew) resetForm(); setEditing(null); }}
          disabled={showArchived}
          style={{ ...btnPrimary, ...(showArchived ? { opacity: 0.4, cursor: 'not-allowed' } : {}) }}
        >
          {showNew ? 'Cancel' : '+ New QR'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fde7e9', color: '#b30021', padding: 10, borderRadius: 6, marginBottom: 12 }}>
          {error}
        </div>
      )}

      {showNew && (
        <form onSubmit={handleCreate} style={card}>
          <h2 style={{ marginTop: 0 }}>New QR</h2>
          <Field label="Name">
            <input name="name" required style={input} placeholder="e.g. cornerstone-procurecon" />
          </Field>
          <Field label="Destination URL">
            <input name="destination_url" required style={input} placeholder="https://example.com/page" />
          </Field>
          <label style={lblRow}>
            <input type="checkbox" name="is_dynamic" value="true" defaultChecked /> &nbsp;Dynamic (editable destination via /r/&lt;slug&gt;)
          </label>

          <fieldset style={{ border: '1px solid #e2e5ec', borderRadius: 6, padding: 12, margin: '12px 0' }}>
            <legend style={{ fontSize: 13, color: '#3a4252', padding: '0 6px' }}>Center logo (optional)</legend>
            <div style={{ display: 'flex', gap: 16, marginBottom: 10, fontSize: 13 }}>
              <label><input type="radio" name="_logo_src" checked={logoSource === 'none'} onChange={() => { setLogoSource('none'); setAnalysis(null); }} /> None</label>
              <label><input type="radio" name="_logo_src" checked={logoSource === 'upload'} onChange={() => { setLogoSource('upload'); setAnalysis(null); }} /> Upload file</label>
              <label><input type="radio" name="_logo_src" checked={logoSource === 'url'} onChange={() => { setLogoSource('url'); setAnalysis(null); }} /> From URL</label>
            </div>
            {logoSource === 'upload' && (
              <input ref={fileRef} type="file" accept="image/*" onChange={() => setAnalysis(null)} />
            )}
            {logoSource === 'url' && (
              <input
                type="url"
                value={logoUrl}
                onChange={(e) => { setLogoUrl(e.target.value); setAnalysis(null); }}
                style={input}
                placeholder="https://example.com/logo.png"
              />
            )}
            {logoSource !== 'none' && (
              <div style={{ marginTop: 10 }}>
                <button type="button" disabled={analyzing} onClick={handleAnalyze} style={btnGhost}>
                  {analyzing ? 'Analyzing…' : analysis ? 'Re-analyze' : 'Analyze logo (extract colors)'}
                </button>
              </div>
            )}
            {analysis && (
              <div style={{ marginTop: 12, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                {analysis.png_url && (
                  <img
                    src={analysis.png_url}
                    alt="logo preview"
                    style={{ width: 64, height: 64, objectFit: 'contain', background: '#f5f6f8', border: '1px solid #e2e5ec', borderRadius: 4 }}
                  />
                )}
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: '#5b6573', marginBottom: 6 }}>Click a swatch to use it as the outer or data color (or override below).</div>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {analysis.palette.map((hex) => (
                      <Swatch key={hex} hex={hex} onPickOuter={() => setQrDark(hex)} onPickData={() => setDataDark(hex)} />
                    ))}
                    {analysis.palette.length === 0 && <span style={{ fontSize: 12, color: '#5b6573' }}>No dominant colors detected.</span>}
                  </div>
                </div>
              </div>
            )}
          </fieldset>

          <div style={twoCol}>
            <Field label="QR (outer) color">
              <input type="color" name="qr_dark" value={qrDark} onChange={(e) => setQrDark(e.target.value)} style={{ ...input, padding: 2, height: 36 }} />
            </Field>
            <Field label="Data (inner) color">
              <input type="color" name="data_dark" value={dataDark} onChange={(e) => setDataDark(e.target.value)} style={{ ...input, padding: 2, height: 36 }} />
            </Field>
          </div>

          <details style={{ fontSize: 13, margin: '6px 0 12px' }}>
            <summary style={{ cursor: 'pointer', color: '#5b6573' }}>Advanced</summary>
            <div style={threeCol}>
              <Field label="Output (px)"><input type="number" name="output_size_px" defaultValue={600} min={100} max={2000} style={input} /></Field>
              <Field label="Logo %"><input type="number" name="logo_percent" defaultValue={20} min={5} max={35} style={input} /></Field>
              <Field label="Quiet zone"><input type="number" name="quiet_zone" defaultValue={4} min={0} max={10} style={input} /></Field>
            </div>
            <label style={{ ...lblRow, gap: 8, marginTop: 10 }}>
              <input type="checkbox" name="black_to_white" value="true" /> Recolor pure-black logo pixels to white
            </label>
          </details>
          <button type="submit" disabled={busy} style={btnPrimary}>
            {busy ? 'Generating…' : 'Generate QR'}
          </button>
        </form>
      )}

      {qrs === null && !error && <p>Loading…</p>}
      {qrs && qrs.length === 0 && !showNew && (
        <p>{showArchived ? 'No archived QRs.' : <>No QRs yet. Click <strong>+ New QR</strong> to create one.</>}</p>
      )}

      {qrs && qrs.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 16 }}>
          {qrs.map((q) => (
            <div key={q.id} style={{ ...card, ...(q.archived_at ? cardArchived : {}) }}>
              {q.png_url && <img src={q.png_url} alt={q.name} style={{ width: '100%', borderRadius: 4, ...(q.archived_at ? { filter: 'grayscale(0.6) opacity(0.75)' } : {}) }} />}
              <h3 style={{ margin: '10px 0 4px', fontSize: 16 }}>{q.name}</h3>
              <p style={{ margin: 0, fontSize: 12, color: '#5b6573', wordBreak: 'break-all' }}>
                {q.is_dynamic && <code>/r/{q.slug}</code>}{q.is_dynamic && ' → '}
                {q.destination_url}
              </p>
              {q.archived_at && (
                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#8a8f99' }}>
                  Archived {new Date(q.archived_at).toLocaleDateString()} · redirect still works
                </p>
              )}
              <div style={{ display: 'flex', gap: 10, marginTop: 10, fontSize: 13, flexWrap: 'wrap' }}>
                {q.archived_at ? (
                  <>
                    <button onClick={() => setStatsForQr(q)} style={btnLink}>Stats</button>
                    {q.png_url && <a href={q.png_url} download style={{ color: '#0d6efd' }}>Download</a>}
                    <button onClick={() => handleUnarchive(q.id)} style={{ ...btnLink, marginLeft: 'auto' }} disabled={busy}>Unarchive</button>
                    <button onClick={() => handleDeletePermanent(q.id, q.name)} style={{ ...btnLink, color: '#b30021' }} disabled={busy}>Delete permanently</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setEditing(q); setShowNew(false); }} style={btnLink}>Edit</button>
                    <button onClick={() => setStatsForQr(q)} style={btnLink}>Stats</button>
                    {q.png_url && <a href={q.png_url} download style={{ color: '#0d6efd' }}>Download</a>}
                    <button onClick={() => handleArchive(q.id)} style={{ ...btnLink, color: '#b30021', marginLeft: 'auto' }} disabled={busy}>
                      Archive
                    </button>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {statsForQr && (
        <QrStatsModal
          qrId={statsForQr.id}
          qrName={statsForQr.name}
          qrCreatedAt={statsForQr.created_at}
          onClose={() => setStatsForQr(null)}
        />
      )}

      {editing && (
        <QrEditModal
          qr={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await refresh(); }}
        />
      )}
    </div>
  );
};

const Swatch: React.FC<{ hex: string; onPickOuter: () => void; onPickData: () => void }> = ({ hex, onPickOuter, onPickData }) => (
  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
    <div
      title={hex}
      style={{ width: 44, height: 32, background: hex, borderRadius: 4, border: '1px solid #cdd3dc', cursor: 'default' }}
    />
    <div style={{ fontSize: 10, color: '#5b6573' }}>{hex}</div>
    <div style={{ display: 'flex', gap: 4 }}>
      <button type="button" onClick={onPickOuter} title="Use as outer/QR color" style={mini}>Out</button>
      <button type="button" onClick={onPickData} title="Use as data/inner color" style={mini}>In</button>
    </div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <label style={{ display: 'block', fontSize: 13, color: '#3a4252', marginBottom: 4 }}>{label}</label>
    {children}
  </div>
);

const input: React.CSSProperties = {
  width: '100%',
  padding: '8px 10px',
  border: '1px solid #cdd3dc',
  borderRadius: 6,
  fontSize: 14,
  boxSizing: 'border-box',
};
const card: React.CSSProperties = {
  background: 'white',
  borderRadius: 8,
  padding: 16,
  boxShadow: '0 1px 3px rgba(0,0,0,.06)',
  marginBottom: 16,
};
const cardArchived: React.CSSProperties = {
  background: '#f6f7fb',
  border: '1px dashed #cdd3dc',
  boxShadow: 'none',
};
const btnPrimary: React.CSSProperties = {
  background: '#0f1a2b',
  color: 'white',
  border: 0,
  padding: '8px 14px',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  background: 'white',
  color: '#3a4252',
  border: '1px solid #cdd3dc',
  padding: '8px 14px',
  borderRadius: 6,
  fontSize: 14,
  cursor: 'pointer',
};
const btnLink: React.CSSProperties = {
  background: 'none',
  border: 0,
  color: '#0d6efd',
  padding: 0,
  fontSize: 13,
  cursor: 'pointer',
};
const mini: React.CSSProperties = {
  background: 'white',
  border: '1px solid #cdd3dc',
  color: '#3a4252',
  borderRadius: 4,
  padding: '1px 6px',
  fontSize: 11,
  cursor: 'pointer',
};
const lblRow: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
  color: '#3a4252',
  margin: '6px 0',
};
const twoCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 };
const threeCol: React.CSSProperties = { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginTop: 10 };

export default QrCodes;
